import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { ShieldCheck, Save, Upload, BookMarked, Trash2, AlertTriangle } from "lucide-react";

export default function Admin() {
  const [tab, setTab] = useState("scans");
  const [scans, setScans] = useState([]);
  const [refs, setRefs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [refName, setRefName] = useState("");
  const [refDesc, setRefDesc] = useState("");
  const [refFile, setRefFile] = useState(null);

  const loadScans = async () => {
    try {
      const { data } = await api.get("/admin/scans");
      setScans(data);
    } catch (err) {
      console.error("Admin: failed to load scans:", err);
    }
  };
  const loadRefs = async () => {
    try {
      const { data } = await api.get("/admin/references");
      setRefs(data);
    } catch (err) {
      console.error("Admin: failed to load references:", err);
    }
  };

  useEffect(() => {
    loadScans();
    loadRefs();
  }, []);

  const pick = (s) => {
    setSelected(s);
    const ai = s.ai_result || {};
    const c = s.correction || {};
    setForm({
      style_name: c.style_name || ai.style_name || "",
      barber_script: c.barber_script || ai.barber_script || "",
      products_json: JSON.stringify(c.products || ai.products || [], null, 2),
      admin_notes: c.admin_notes || "",
      save_as_reference: false,
    });
  };

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    setMsg("");
    let parsedProducts;
    try {
      parsedProducts = form.products_json ? JSON.parse(form.products_json) : null;
    } catch (_) {
      setBusy(false);
      setMsg("Products JSON is invalid.");
      return;
    }
    try {
      await api.post(`/admin/scans/${selected.id}/correction`, {
        style_name: form.style_name || null,
        barber_script: form.barber_script || null,
        products: parsedProducts,
        admin_notes: form.admin_notes || null,
        save_as_reference: !!form.save_as_reference,
      });
      setMsg("Saved. AI will use this in future scans.");
      await loadScans();
      await loadRefs();
    } catch (e) {
      setMsg("Failed to save correction.");
    } finally {
      setBusy(false);
    }
  };

  const uploadRef = async () => {
    if (!refFile || !refName) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", refFile);
      await api.post(`/admin/references?style_name=${encodeURIComponent(refName)}&description=${encodeURIComponent(refDesc)}`, fd);
      setRefName("");
      setRefDesc("");
      setRefFile(null);
      await loadRefs();
    } catch (err) {
      console.error("Admin: reference upload failed:", err);
    } finally {
      setBusy(false);
    }
  };

  const deleteRef = async (id) => {
    if (!confirm("Delete this reference?")) return;
    await api.delete(`/admin/references/${id}`);
    await loadRefs();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16 animate-fade-in">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-copper" />
        <p className="cm-label">ADMIN · TRAINING DASHBOARD</p>
      </div>
      <h1 className="cm-heading text-4xl sm:text-5xl mt-3">Sharpen the <span className="text-copper">model</span>.</h1>

      <div className="mt-8 flex gap-3">
        <button
          onClick={() => setTab("scans")}
          className={`cm-btn-secondary ${tab === "scans" ? "border-copper text-copper" : ""}`}
          data-testid="admin-tab-scans"
        >
          Review scans
        </button>
        <button
          onClick={() => setTab("refs")}
          className={`cm-btn-secondary ${tab === "refs" ? "border-copper text-copper" : ""}`}
          data-testid="admin-tab-refs"
        >
          Reference library
        </button>
      </div>

      {tab === "scans" && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <p className="cm-label mb-4">All scans ({scans.length})</p>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
              {scans.map((s) => (
                <button
                  key={s.id}
                  onClick={() => pick(s)}
                  data-testid={`admin-scan-${s.id}`}
                  className={`w-full text-left cm-card p-4 flex gap-3 hover:border-copper/50 transition-all ${
                    selected?.id === s.id ? "border-copper" : ""
                  }`}
                >
                  <img
                    src={fileUrl(s.storage_path)}
                    alt=""
                    className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">
                      {s.ai_result?.style_name || (s.ai_result?.error ? "Unrecognized" : "Untitled")}
                    </p>
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                      {new Date(s.created_at).toLocaleString()}
                    </p>
                    {s.admin_corrected && (
                      <span className="text-[10px] uppercase tracking-widest text-copper mt-1 block">corrected</span>
                    )}
                  </div>
                </button>
              ))}
              {scans.length === 0 && <p className="text-gray-500 text-sm">No scans yet.</p>}
            </div>
          </div>

          <div className="lg:col-span-8">
            {selected ? (
              <div className="cm-card p-6">
                <img
                  src={fileUrl(selected.storage_path)}
                  alt=""
                  className="rounded-lg w-full max-h-96 object-contain bg-ink-800"
                />
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="cm-label block mb-2">Style name</label>
                    <input
                      value={form.style_name || ""}
                      onChange={(e) => setForm({ ...form, style_name: e.target.value })}
                      className="cm-input"
                      data-testid="admin-style-name-input"
                    />
                  </div>
                  <div>
                    <label className="cm-label block mb-2">Barber script</label>
                    <textarea
                      rows={5}
                      value={form.barber_script || ""}
                      onChange={(e) => setForm({ ...form, barber_script: e.target.value })}
                      className="cm-input"
                      data-testid="admin-barber-script-input"
                    />
                  </div>
                  <div>
                    <label className="cm-label block mb-2">Products (JSON array)</label>
                    <textarea
                      rows={6}
                      value={form.products_json || ""}
                      onChange={(e) => setForm({ ...form, products_json: e.target.value })}
                      className="cm-input font-mono text-xs"
                      data-testid="admin-products-input"
                    />
                  </div>
                  <div>
                    <label className="cm-label block mb-2">Admin notes</label>
                    <textarea
                      rows={2}
                      value={form.admin_notes || ""}
                      onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
                      className="cm-input"
                      data-testid="admin-notes-input"
                    />
                  </div>
                  <label className="flex items-center gap-3 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={!!form.save_as_reference}
                      onChange={(e) => setForm({ ...form, save_as_reference: e.target.checked })}
                      data-testid="admin-save-reference-checkbox"
                    />
                    <BookMarked className="w-4 h-4 text-copper" />
                    Save as a reference example (AI will use it on future scans)
                  </label>
                  {msg && (
                    <div className="flex items-center gap-2 text-sm text-copper">
                      <AlertTriangle className="w-4 h-4" /> {msg}
                    </div>
                  )}
                  <button
                    onClick={save}
                    disabled={busy}
                    className="cm-btn-primary inline-flex items-center gap-2"
                    data-testid="admin-save-correction-button"
                  >
                    <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save correction"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="cm-card p-10 text-center text-gray-500">
                Pick a scan on the left to review and correct.
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "refs" && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="cm-card p-6 lg:col-span-5">
            <p className="cm-label">UPLOAD A REFERENCE STYLE</p>
            <p className="text-xs text-gray-500 mt-2">The AI uses these to anchor its style names + scripts.</p>
            <div className="mt-5 space-y-4">
              <input
                value={refName}
                onChange={(e) => setRefName(e.target.value)}
                placeholder="Style name (e.g., 'Mid Fade Textured Crop')"
                className="cm-input"
                data-testid="admin-ref-name-input"
              />
              <textarea
                rows={4}
                value={refDesc}
                onChange={(e) => setRefDesc(e.target.value)}
                placeholder="Short description / ideal use / notes…"
                className="cm-input"
                data-testid="admin-ref-desc-input"
              />
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setRefFile(e.target.files?.[0] || null)}
                className="block text-sm"
                data-testid="admin-ref-file-input"
              />
              <button
                onClick={uploadRef}
                disabled={!refName || !refFile || busy}
                className="cm-btn-primary inline-flex items-center gap-2"
                data-testid="admin-ref-upload-button"
              >
                <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload reference"}
              </button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="cm-label mb-4">Reference library ({refs.length})</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {refs.map((r) => (
                <div key={r.id} className="cm-card p-4" data-testid={`admin-ref-${r.id}`}>
                  <img
                    src={fileUrl(r.storage_path)}
                    alt={r.style_name}
                    className="w-full h-40 object-cover rounded-md"
                  />
                  <p className="cm-heading text-lg mt-3">{r.style_name}</p>
                  <p className="text-xs text-gray-500 mt-2 line-clamp-3">{r.description}</p>
                  <button
                    onClick={() => deleteRef(r.id)}
                    className="cm-btn-secondary mt-4 inline-flex items-center gap-2 text-xs"
                    data-testid={`admin-ref-delete-${r.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              ))}
              {refs.length === 0 && <p className="text-gray-500 text-sm">No references yet.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
